/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import Cron, { CronProps, PeriodType } from 'react-js-cron';
import { SelectField, SelectFieldProps } from '../FormFields/fields/Select';
import { CronEditorProps } from './CronEditor.props';

/**
 * Cron editor.
 */
export const CronEditor = (props: CronEditorProps): JSX.Element => {
  const { defaultPeriod, setDefaultPeriod, setValue, value, ...rest } = props;

  // Initial period to be specified on the select field component
  const [initialPeriod, setInitialPeriod] = useState('');

  useEffect(() => {
    if (initialPeriod) {
      setDefaultPeriod(initialPeriod as PeriodType);
    }
  }, [initialPeriod, setDefaultPeriod, value]);

  const cronEditorClass = clsx(`sds-cron-editor-container`);

  /* eslint-disable sort-keys-fix/sort-keys-fix */
  const cronProps: CronProps = {
    allowedDropdowns: ['period', 'months', 'month-days', 'week-days', 'hours', 'minutes'],
    allowedPeriods: ['year', 'month', 'week', 'day', 'hour', 'minute'],
    className: 'sds-cron',

    clearButton: false,
    defaultPeriod: defaultPeriod as PeriodType,
    humanizeLabels: true,
    locale: {
      altWeekDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],

      // Capitalise options and placeholder values
      yearOption: 'Year',
      monthOption: 'Month',
      weekOption: 'Week',
      dayOption: 'Day',
      hourOption: 'Hour',
      minuteOption: 'Minute',

      emptyMonths: 'Every month',
      emptyMonthDays: 'Every day of the month',
      emptyWeekDays: 'Every day of the week',
      emptyHours: 'Every hour',
      emptyMinutes: 'Every minute',
      emptyMinutesForHourPeriod: 'Every',

      emptyMonthDaysShort: 'Day of the month',
      emptyWeekDaysShort: 'Day of the week',
    },
    setValue,
    value,
    ...rest,
  };
  /* eslint-enable sort-keys-fix/sort-keys-fix */

  const ref = useRef<HTMLInputElement | null>(null);

  const selectProps: SelectFieldProps = {
    callback: (option) => {
      setInitialPeriod(option.value);
    },
    fieldLabel: 'Period',
    hideLabel: true,
    id: 'cron-editor__period',
    name: 'cron-editor__period',
    options: ['Year', 'Month', 'Week', 'Day', 'Hour', 'Minute'].map((item) => {
      return {
        key: item.toLowerCase(),
        label: item,
        value: item.toLowerCase(),
      };
    }),
    placeholder: 'Time period',
  };

  const getComponent = (): JSX.Element => {
    if (!value && defaultPeriod === undefined) {
      // This component will only be rendered if a default value has not been set,
      // and until a time period has been chosen.
      return (
        <div className="cron-editor-default">
          <span className="cron-editor-default__label">Every</span>
          <SelectField {...selectProps} ref={ref} />
        </div>
      );
    }

    return (
      <div className="cron-editor-container">
        <Cron {...cronProps} />
      </div>
    );
  };

  return <div className={cronEditorClass}>{getComponent()}</div>;
};
